export function initAffiliateBanner(settings, i18n, discountConfigTxt, cartDiscountTitle, fetchDiscountFn) {
  const controller = new AffiliateBannerController(settings, i18n, discountConfigTxt, cartDiscountTitle, fetchDiscountFn);
  controller.init();
}

class AffiliateBannerController {
  constructor(settings, i18n, discountConfigTxt, cartDiscountTitle, fetchDiscountFn) {
    this.settings = settings || {};
    this.i18n = i18n || {};
    this.cartDiscountTitle = cartDiscountTitle || null;
    this.hasLocal = detectLocalStorageSupport();

    const parsed = parseDiscountConfigText(discountConfigTxt);
    this.nameResolver = createNameResolver(parsed.prefixes, parsed.names);

    this.respTtlMs = computeResponseTtlMs(this.settings.responseCacheMinutes);
    this.fetchDiscount = typeof fetchDiscountFn === 'function' ? fetchDiscountFn : null;
  }

  init(){
    const affCode = extractAffiliateCode(this.cartDiscountTitle);
    if (affCode) { this.setSession(affCode); }

    const code = affCode || this.getSession();
    if (code) {
      this.resolveAndShow(code);
    } else {
      fetchCartDiscountCode().then((c) => {
        if (!c) { this.hide(); return; }
        this.setSession(c);
        this.resolveAndShow(c);
      });
    }

    this.attachEvents();
  }

  attachEvents(){
    window.addEventListener('pageshow', () => {
      const c = extractAffiliateCode(this.cartDiscountTitle) || this.getSession();
      if (c) { this.resolveAndShow(c); }
    });
    window.addEventListener('hashchange', () => {
      const c = extractAffiliateCode(this.cartDiscountTitle) || this.getSession();
      if (c) { this.resolveAndShow(c); }
    });
    window.addEventListener('shopify:cart-updated', () => {
      const reader = (window.CartAPI && window.CartAPI.fetchCartDiscountCode)
        ? window.CartAPI.fetchCartDiscountCode()
        : fetchCartDiscountCode();
      reader.then((c) => {
        if (!c) { return; }
        try { this.setSession(c); } catch(e){}
        this.resolveAndShow(c);
      });
    });
  }

  resolveAndShow(code){
    if (!code) { this.hide(); return; }
    try { this.show(optimisticMessage(code, this.i18n, this.nameResolver)); } catch(e){}
    this.updateBanner(code);
  }

  updateBanner(code){
    if (!code) return;

    // Check cache first
    try {
      const cached = getRespCache(code, this.hasLocal);
      if (cached) {
        if (isCodeExpired(cached.entry)) {
          // Code expired, clean up all related keys and hide banner
          this.cleanupExpiredCode(code);
          this.hide();
          return;
        }
        if (isCodeNotStarted(cached.entry)) {
          // Code not started yet, show message
          this.show(notStartedMessage(cached.entry, this.i18n, this.nameResolver));
          return;
        }
        this.showBannerWithEntry(cached.entry, code);
        return;
      }
    } catch(e){}

    // No cache or expired, fetch from API
    if (!this.fetchDiscount) {
      console.warn('[AffiliateBanner] fetchDiscount function not provided; showing optimistic banner only.');
      this.show(optimisticMessage(code, this.i18n, this.nameResolver));
      return;
    }

    this.fetchDiscount(code, this.settings.appsScriptUrl)
      .then(entry => this.handleApiResponse(entry, code))
      .catch(() => this.show(optimisticMessage(code, this.i18n, this.nameResolver)));
  }

  showBannerWithEntry(entry, code) {
    if (!entry.name) entry.name = this.nameResolver(code) || formatAffiliateName(code);
    this.show(formatMessage(entry, this.i18n, this.nameResolver));
  }

  handleApiResponse(entry, code) {
    if (!entry || entry.value == null) {
      this.show(optimisticMessage(code, this.i18n, this.nameResolver));
      return;
    }
    if (isCodeExpired(entry)) {
      this.cleanupExpiredCode(code);
      this.hide();
      return;
    }
    if (isCodeNotStarted(entry)) {
      this.show(notStartedMessage(entry, this.i18n, this.nameResolver));
      try { setRespCache(code, entry, this.hasLocal, this.respTtlMs); } catch(e){}
      return;
    }
    this.showBannerWithEntry(entry, code);
    try { setRespCache(code, entry, this.hasLocal, this.respTtlMs); } catch(e){}
  }

  cleanupExpiredCode(code) {
    try {
      // Clean up affiliate session
      const affKey = 'aff_code';
      localStorage.removeItem(affKey);
      sessionStorage.removeItem(affKey);
      
      // Clean up response cache
      const respKey = 'aff_resp_' + String(code).toLowerCase();
      localStorage.removeItem(respKey);
      sessionStorage.removeItem(respKey);
      
      console.log('[AffiliateBanner] Cleaned up expired code:', code);
    } catch(e) {
      console.warn('[AffiliateBanner] Error cleaning up expired code:', e);
    }
  }

  setSession(code){
    const ttl = computeTtlMs(this.settings.ttlDays);
    setAffiliateSession(code, ttl, this.hasLocal);
  }

  getSession(){
    return getAffiliateSession(this.hasLocal);
  }

  show(text){ showBanner(text); }
  hide(){ hideBanner(); }
}

function detectLocalStorageSupport(){
  try{ localStorage.setItem('__t','1'); localStorage.removeItem('__t'); return true; }catch(e){ return false; }
}

function parseDiscountConfigText(discountConfigTxt){
  try{
    if (discountConfigTxt && typeof discountConfigTxt === 'object'){
      const cfg = discountConfigTxt;
      return { prefixes: cfg.prefixes || [], names: cfg.names || {} };
    }
  }catch(e){}

  const raw = typeof discountConfigTxt === 'string' ? discountConfigTxt : String(discountConfigTxt || '');
  if (!raw || raw.trim() === ''){
    console.warn('Article/Metaobject current_discounts vide, utilisation des données de fallback');
    return { prefixes: [], names: {} };
  }
  try{
    const cleanJson = raw.replace(/<[^>]*>/g, '').trim();
    const cfg = JSON.parse(cleanJson);
    return { prefixes: cfg.prefixes || [], names: cfg.names || {} };
  }catch(e){
    console.warn('JSON invalide, tentative de correction...', e);
    try{
      const fixed = raw
        .replace(/<[^>]*>/g, '')
        .replace(/(\w+):/g, '"$1":')
        .replace(/'/g, '"')
        .trim();
      const cfg = JSON.parse(fixed);
      return { prefixes: cfg.prefixes || [], names: cfg.names || {} };
    }catch(e2){
      console.error('Impossible de corriger le JSON:', e2, 'Contenu original:', raw);
      return { prefixes: [], names: {} };
    }
  }
}

function createNameResolver(prefixes, map){
  const pfx = Array.isArray(prefixes) ? prefixes : [];
  const m = map || {};
  return function(code){
    const lc = String(code||'').toLowerCase();
    for (let i=0;i<pfx.length;i++){
      const p = pfx[i];
      if (lc.indexOf(p) === 0){
        const slug = lc.slice(p.length).split('-')[0];
        return m[slug] || null;
      }
    }
    return null;
  };
}

function slugFromUrl(url){
  try{
    const loc = new URL(url, window.location.origin);
    const q = loc.searchParams.get('discount');
    if(q){ return q; }
    if (loc.hash && loc.hash.indexOf('discount=') >= 0) {
      const m = loc.hash.match(/(?:[#&?])discount=([^&]*)/);
      if (m && m[1]) { return decodeURIComponent(m[1]); }
    }
    const parts = (loc.pathname||'').split('/').filter(Boolean);
    const idx = parts.indexOf('discount');
    if(idx>=0 && idx+1<parts.length){ return decodeURIComponent(parts[idx+1]||''); }
  }catch(e){}
  return null;
}

function extractAffiliateCode(cartDiscountTitle){
  const here = slugFromUrl(window.location.href);
  if (here) { return here; }
  if (document.referrer){
    const fromRef = slugFromUrl(document.referrer);
    if (fromRef) { return fromRef; }
  }
  if (cartDiscountTitle) { return cartDiscountTitle; }
  return null;
}

function nowMs(){ 
  return Date.now ? Date.now() : new Date().getTime(); 
}

function computeTtlMs(ttlDays){
  const d = parseInt(ttlDays||0,10);
  return d>0 ? d*24*60*60*1000 : 0;
}

function computeResponseTtlMs(cacheMinutes){
  const minutes = parseInt(cacheMinutes||30,10);
  return minutes > 0 ? minutes*60*1000 : 30*60*1000; // Default to 30 minutes if invalid
}

function setAffiliateSession(code, ttlMs, hasLocal){
  try{
    const payload = { code: code, ts: nowMs(), ttl: parseInt(ttlMs||0,10) };
    const key = 'aff_code';
    if (hasLocal) { try { localStorage.setItem(key, JSON.stringify(payload)); } catch(e){} }
    try { sessionStorage.setItem(key, JSON.stringify(payload)); } catch(e){}
  }catch(e){}
}

function getAffiliateSession(hasLocal){
  try{
    const key = 'aff_code';
    let newest = null;
    let payload = null;
    if (hasLocal){
      const raw = localStorage.getItem(key);
      if (raw){
        try{ payload = JSON.parse(raw); }catch(e){ localStorage.removeItem(key); }
      }
    }
    if (!payload){
      try{
        const rawS = sessionStorage.getItem(key);
        if (rawS){ payload = JSON.parse(rawS); }
      }catch(e){ sessionStorage.removeItem(key); }
    }

    if (payload && payload.code){
      const ttl = parseInt(payload.ttl||0,10);
      if (!ttl || (payload.ts && (nowMs() - payload.ts) < ttl)){
        newest = payload.code;
      } else {
        try { localStorage.removeItem(key); }catch(e){}
        try { sessionStorage.removeItem(key); }catch(e){}
      }
    } else if (typeof payload === 'string') {
      newest = payload;
    }
    return newest;
  }catch(e){}
  return null;
}

function showBanner(text){
  const el = document.getElementById('affiliate-banner'); if(!el) return;
  const span = document.getElementById('affiliate-banner-text'); if(span) span.innerHTML = text || '';
  el.style.display = 'block';
  try { document.body.classList.add('aff-banner-open'); } catch(e){}
}

function hideBanner(){
  const el = document.getElementById('affiliate-banner'); if(!el) return;
  const span = document.getElementById('affiliate-banner-text'); if(span) span.textContent='';
  el.style.display = 'none';
  try { document.body.classList.remove('aff-banner-open'); } catch(e){}
}

function formatAffiliateName(code){
  return String(code||'')
    .replace(/[-_]/g, ' ')
    .toUpperCase();
}

function escapeHtml(input){
  try{
    return String(input == null ? '' : input)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }catch(e){ return ''; }
}

function decodeHtmlEntities(input){
  try{
    return String(input == null ? '' : input)
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&');
  }catch(e){ return String(input || ''); }
}

function isCodeExpired(entry){
  if (!entry) return false;
  try {
    const now = new Date();
    
    // Check if code has ended
    if (entry.ends_at) {
      const endsAt = new Date(entry.ends_at);
      if (now > endsAt) return true;
    }
    
    return false;
  } catch(e) {
    console.warn('Erreur lors de la vérification de la date d\'expiration:', e);
    return false;
  }
}

function isCodeNotStarted(entry){
  if (!entry) return false;
  try {
    const now = new Date();
    
    // Check if code hasn't started yet
    if (entry.starts_at) {
      const startsAt = new Date(entry.starts_at);
      if (now < startsAt) return true;
    }
    
    return false;
  } catch(e) {
    console.warn('Erreur lors de la vérification de la date de début:', e);
    return false;
  }
}

function optimisticMessage(code, i18n, nameResolver){
  const name = nameResolver(code) || formatAffiliateName(code);
  const safeName = escapeHtml(name);
  return i18n.active_name.replace(/\{\{\s*affiliated_person\s*\}\}/g, safeName);
}

function notStartedMessage(entry, i18n, nameResolver){
  const name = nameResolver(entry.code) || formatAffiliateName(entry.name) || '';
  const safeName = escapeHtml(name);
  
  let startDate = '';
  if (entry.starts_at) {
    try {
      const date = new Date(entry.starts_at);
      const pageLang = document.documentElement.lang || 'fr-FR';
      startDate = date.toLocaleDateString(pageLang, { day: 'numeric', month: 'long' });
    } catch(e) { 
      console.warn('Erreur lors du formatage de la date de début:', e); 
    }
  }
  
  const safeStartDate = escapeHtml(startDate);
  const template = i18n.banner_not_started || 'Code {{affiliated_person}} sera actif à partir du {{start_date}}';
  return template
    .replace(/\{\{\s*affiliated_person\s*\}\}/g, safeName)
    .replace(/\{\{\s*start_date\s*\}\}/g, safeStartDate);
}

function formatMessage(entry, i18n, nameResolver){
  entry = entry || {};
  const name = nameResolver(entry.code) || formatAffiliateName(entry.name) || '';
  if (entry.value != null && entry.type) {
    const isPercent = String(entry.type) === 'percentage';
    let tmpl = isPercent ? i18n.percent : i18n.fixed;
    if (entry.ends_at) { tmpl = isPercent ? i18n.percent_with_end_date : i18n.fixed_with_end_date; }

    let endDate = '';
    if (entry.ends_at) {
      try {
        const date = new Date(entry.ends_at);
        const pageLang = document.documentElement.lang || 'fr-FR';
        // endDate = date.toLocaleDateString(pageLang, { day: 'numeric', month: 'long', year: 'numeric' });
        endDate = date.toLocaleDateString(pageLang, { day: 'numeric', month: 'long' });
      } catch(e) { console.warn('Erreur lors du formatage de la date de fin:', e); }
    }

    // Decode entities present in the translation string (so markup like <span> renders)
    const htmlTmpl = decodeHtmlEntities(tmpl);

    const safeName = escapeHtml(name);
    const safeValue = escapeHtml(entry.value);
    const safeEndDate = escapeHtml(endDate);

    return htmlTmpl
      .replace(/\{\{\s*reduction\s*\}\}/g, safeValue)
      .replace(/\{\{\s*affiliated_person\s*\}\}/g, safeName)
      .replace(/\{\{\s*end_date\s*\}\}/g, safeEndDate);
  }
  return name ? i18n.active_name.replace(/\{\{\s*affiliated_person\s*\}\}/g, escapeHtml(name)) : i18n.active;
}

async function fetchCartDiscountCode(){
  try{
    const r = await fetch('/cart.js', { credentials: 'same-origin' });
    if(!r.ok) return null;
    const cart = await r.json();
    if (cart && Array.isArray(cart.cart_level_discount_applications) && cart.cart_level_discount_applications.length){
      const app = cart.cart_level_discount_applications[0];
      if (app && app.title) return app.title;
    }
    if (cart && Array.isArray(cart.discount_codes) && cart.discount_codes.length){
      const dc = cart.discount_codes.find(function(d){ return d && (d.applied || d.applicable || typeof d.amount !== 'undefined'); }) || cart.discount_codes[0];
      if (dc && dc.code) return dc.code;
    }
  } catch(e){}
  return null;
}

function getRespCache(code, hasLocal){
  const cacheKey = 'aff_resp_' + String(code).toLowerCase();
  const now = Date.now();
  let obj = null;
  try{
    if (hasLocal){
      const rawL = localStorage.getItem(cacheKey);
      if (rawL){ obj = JSON.parse(rawL); }
    }
  }catch(e){}
  if (!obj){
    try{
      const rawS = sessionStorage.getItem(cacheKey);
      if (rawS){ obj = JSON.parse(rawS); }
    }catch(e){}
  }
  if (obj && obj.until && now < obj.until){ return obj; }
  try{
    if (obj){
      if (hasLocal){ localStorage.removeItem(cacheKey); }
      sessionStorage.removeItem(cacheKey);
    }
  }catch(e){}
  return null;
}

function setRespCache(code, entry, hasLocal, respTtlMs){
  const cacheKey = 'aff_resp_' + String(code).toLowerCase();
  const obj = { until: Date.now() + (respTtlMs || 30*60*1000), entry: entry };
  try{ sessionStorage.setItem(cacheKey, JSON.stringify(obj)); }catch(e){}
  if (hasLocal){ try{ localStorage.setItem(cacheKey, JSON.stringify(obj)); }catch(e){} }
}